import { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import type { AuthUser, OrganizationLimits } from '../../types/user';
import { userInitials, userName } from '../../types/user';
import {
    BellIcon,
    ChevronDownIcon,
    DatabaseIcon,
    GearIcon,
    LogoutIcon,
    OrgIcon,
    SearchIcon,
} from '../../assets/icons/icons';
import { cx } from '../../utils/cx';
import styles from './Topbar.module.scss';
import { useGetUserNotificationsQuery } from '../../entities/notifications/api/notificationsApi';
import { useSeenNotifications } from '../../entities/notifications/model/seen';
import { formatYekb, isActive, useNowYekb } from '../../entities/notifications/model/window';
import { useGetRequestLimitQuery } from '../../entities/limits/api/limitsApi';
import { isAdmin } from '../../shared/lib/access';
import { ChangePasswordModal } from '../ChangePasswordModal/ChangePasswordModal';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { openOrganizationSwitcher, selectOrganizationId } from '../../entities/organization/model/slice';
import { LogoutModal } from '../ui/LogoutModal/LogoutModal';
import { Tooltip } from '../ui/Tooltip/Tooltip';

type Menu = 'notifications' | 'profile' | null;

interface TopbarProps {
    date: string;
    user?: AuthUser | null;
    limits?: OrganizationLimits;
    onSearch?: (query: string) => void;
    onLogout?: () => void;
}

const LIMIT_POLL_MS = 15000;
const NOTIFICATIONS_PREVIEW = 5;
const NOTIFICATIONS_POLL_MS = 60_000;
const NOTIFICATIONS_PATH = '/user/notifications';

const formatNumber = (value: number): string =>
    value.toLocaleString('ru-RU').replace(/\s/g, '\u00a0');

const getGreeting = (hours: number = new Date().getHours()): string => {
    if (hours >= 5 && hours < 12) return 'Доброе утро';
    if (hours >= 12 && hours < 18) return 'Добрый день';
    if (hours >= 18 && hours < 23) return 'Добрый вечер';
    return 'Доброй ночи';
};

export const Topbar: FC<TopbarProps> = ({
    date,
    user,
    // organization,
    limits,
    onSearch,
    onLogout,
}) => {
    const [menu, setMenu] = useState<Menu>(null);
    const [query, setQuery] = useState('');
    const [passwordOpen, setPasswordOpen] = useState(false);
    const [logoutOpen, setLogoutOpen] = useState(false);
    const dispatch = useAppDispatch();

    const userId = user?.id ?? user?.email;

    const { data: notifications } = useGetUserNotificationsQuery(undefined, {
        skip: !userId,
        pollingInterval: NOTIFICATIONS_POLL_MS,
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
    });

    const { seen, markSeen } = useSeenNotifications(userId);
    const now = useNowYekb();

    const notificationItems = useMemo(() => notifications?.items ?? [], [notifications]);

    const visibleNotifications = useMemo(() => {
        const active = notificationItems.filter((item) => isActive(item, now));
        const past = notificationItems.filter((item) => !isActive(item, now));
        return active.concat(past);
    }, [notificationItems, now]);

    const unreadIds = useMemo(
        () =>
            notificationItems
                .filter((item) => isActive(item, now) && !seen.has(item.id))
                .map((item) => item.id),
        [notificationItems, now, seen],
    );

    const unreadCount = unreadIds.length;
    const hasUnread = unreadCount > 0;

    const organizationId = useAppSelector(selectOrganizationId);

    const [limitFailed, setLimitFailed] = useState(false);

    const { data: limit, isError: limitError } = useGetRequestLimitQuery(
        { organizationId: organizationId ?? '', admin: isAdmin(user) },
        {
            skip: !organizationId,
            pollingInterval: limitFailed ? 0 : LIMIT_POLL_MS,
        },
    );

    useEffect(() => {
        setLimitFailed(limitError);
    }, [limitError]);

    const navigate = useNavigate();
    const actionsRef = useOutsideClick<HTMLDivElement>(() => setMenu(null));

    const reached = limits?.reached ?? limit?.used ?? 0;
    const maxLimit = limits?.maxLimit ?? limit?.max ?? 0;

    const toggle = (next: Exclude<Menu, null>): void =>
        setMenu((current) => (current === next ? null : next));

    const hasLimit = Boolean(limits) || Boolean(limit);

    const limitLabel = hasLimit
        ? `${formatNumber(reached)} из ${formatNumber(maxLimit)}`
        : limitError
            ? 'нет доступа'
            : '—';

    const usedPercent = hasLimit && maxLimit > 0 ? Math.min((reached / maxLimit) * 100, 100) : 0;

    const isReached = Boolean(limit?.isReached);
    const isWarning = Boolean(limit?.isWarning) && !isReached;

    const limitHint = limitError
        ? 'У вашей учётной записи нет доступа к этой организации — лимит прочитать не удалось'
        : !hasLimit
            ? 'Лимит запросов организации загружается'
            : isReached
                ? 'Лимит запросов исчерпан — поиск недоступен до обновления лимита'
                : isWarning
                    ? `Осталось ${formatNumber(limit?.remaining ?? 0)} — больше половины лимита израсходовано`
                    : 'Лимит запросов организации';

    const go = (path: string): void => {
        setMenu(null);
        navigate(path);
    };

    return (
        <header className={styles.topbar}>
            <div className={styles.identity}>
                <div className={styles.date}>{date}</div>
                <div className={styles.greeting}>{getGreeting()}, {userName(user)}</div>
            </div>

            {limit?.organizationName && (
                <div className={styles.organization}>
                    <div className={styles.organizationLabel}>Текущая организация · ИНН {limit.organizationInn}</div>
                    <Tooltip text={limit.organizationName} align="right" className={styles.orgTip}>
                        <div className={styles.organizationName}>
                            <OrgIcon />
                            <span>{limit.organizationName}</span>
                        </div>
                    </Tooltip>
                </div>
            )}

            <div
                className={cx(
                    styles.counter,
                    isWarning && styles.counterWarning,
                    isReached && styles.counterReached,
                )}
                title={limitHint}
            >
                <DatabaseIcon />

                <div className={styles.counterCopy}>
                    <div className={styles.counterLabel}>Лимит запросов</div>
                    <div className={styles.counterValue}>{limitLabel}</div>

                    <div className={styles.counterTrack}>
                        <i style={{ width: `${usedPercent}%` }} />
                    </div>
                </div>
            </div>

            {onSearch && (
                <div className={styles.search}>
                    <SearchIcon />

                    <input
                        type="search"
                        placeholder="Поиск проекта по названию или ФИО"
                        value={query}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            onSearch(event.target.value);
                        }}
                        aria-label="Поиск проекта"
                    />
                </div>
            )}

            <div className={styles.actions} ref={actionsRef}>
                <button
                    type="button"
                    className={cx(styles.topButton, hasUnread && styles.topButtonMarked)}
                    aria-label={
                        hasUnread
                            ? `Уведомления, непрочитанных: ${unreadCount}`
                            : 'Уведомления'
                    }
                    aria-expanded={menu === 'notifications'}
                    onClick={() => toggle('notifications')}
                >
                    <BellIcon />
                </button>

                {menu === 'notifications' && (
                    <div className={styles.dropdown}>
                        <div className={styles.dropdownHead}>
                            <b>Уведомления</b>
                        </div>

                        {visibleNotifications.length ? (
                            <div className={styles.dropList}>
                                {visibleNotifications.slice(0, NOTIFICATIONS_PREVIEW).map((item) => (
                                    <div
                                        key={item.id}
                                        className={cx(
                                            styles.dropNote,
                                            isActive(item, now) && !seen.has(item.id) && styles.dropNoteUnread,
                                        )}
                                    >
                                        <BellIcon />
                                        <div className={styles.dropNoteCopy}>
                                            <p title={item.text}>{item.text}</p>
                                            <small>
                                                {isActive(item, now)
                                                    ? `до ${formatYekb(item.endsAt)}`
                                                    : formatYekb(item.startsAt)}
                                            </small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.dropInfo}>
                                <BellIcon />
                                Уведомления отсутствуют
                            </div>
                        )}

                        <div className={styles.dropdownFoot}>
                            {hasUnread && (
                                <button
                                    type="button"
                                    className={styles.dropLink}
                                    onClick={() => markSeen(unreadIds)}
                                >
                                    Прочитать всё
                                </button>
                            )}

                            <button
                                type="button"
                                className={styles.dropLink}
                                onClick={() => go(NOTIFICATIONS_PATH)}
                            >
                                Смотреть всё
                            </button>
                        </div>
                    </div>
                )}

                <button
                    type="button"
                    className={`${styles.topButton} ${styles.profileButton}`}
                    aria-label="Профиль и настройки"
                    aria-expanded={menu === 'profile'}
                    onClick={() => toggle('profile')}
                >
                    <span className={styles.miniAvatar}>{userInitials(user)}</span>
                    <ChevronDownIcon className={styles.chevron} />
                </button>

                {menu === 'profile' && (
                    <div className={styles.dropdown}>
                        <div className={styles.dropdownProfile}>
                            <b>{limit?.organizationName ?? ''}</b>
                            <span>{user?.email}</span>
                        </div>

                        <button
                            type="button"
                            className={styles.dropItem}
                            onClick={() => {
                                setMenu(null);
                                setPasswordOpen(true);
                            }}
                        >
                            <GearIcon />
                            Изменить пароль
                        </button>

                        <div className={styles.dropInfo}>
                            <DatabaseIcon />
                            Лимит запросов: {limitLabel}
                        </div>

                        <button
                            type="button"
                            className={styles.dropItem}
                            onClick={() => {
                                setMenu(null);
                                dispatch(openOrganizationSwitcher());
                            }}
                        >
                            <OrgIcon />
                            Изменить организацию
                        </button>

                        <div className={styles.dropSeparator} />

                        <button
                            type="button"
                            className={`${styles.dropItem} ${styles.dropItemDanger}`}
                            onClick={() => {
                                setMenu(null);
                                setLogoutOpen(true);
                            }}
                        >
                            <LogoutIcon />
                            Выход
                        </button>
                    </div>
                )}
            </div>

            <LogoutModal
                open={logoutOpen}
                onClose={() => setLogoutOpen(false)}
                onConfirm={() => {
                    setLogoutOpen(false);
                    onLogout?.();
                }}
            />

            <ChangePasswordModal
                open={passwordOpen}
                onClose={() => setPasswordOpen(false)}
                onSuccess={() => {
                    setPasswordOpen(false);
                    onLogout?.();
                }}
            />
        </header>
    );
};